import type { ApprovalRequest } from '@/lib/types';
import { formatDate } from '../format';
import { humanizeBucket } from '../labels';
import { BucketShell } from './_BucketShell';
import { readProcessedBy, readResolvedAt, readStructuredFields } from './_payload';

export interface PostedViewProps {
  req: ApprovalRequest;
  embedded?: boolean;
}

interface OracleResultV1 {
  purchase_invoice_id?: string | null;
  ref_no?: string | null;
  oracle_document_no?: string | number | null;
  document_no?: string | number | null;
  pi_book_id?: string | null;
  gl_date?: string | null;
  due_date?: string | null;
  post_verified_at?: string | null;
  oracle_purchase_invoice_id?: string | null;
  oracle_purch_invoice_ref_no?: string | null;
  oracle_pi_book_id?: string | null;
  oracle_gl_date?: string | null;
  oracle_due_date?: string | null;
  oracle_post_verified_at?: string | null;
}

function readOracleResult(req: ApprovalRequest): OracleResultV1 {
  const sf = readStructuredFields(req) as Record<string, unknown>;
  const oracleResult = sf['oracle_result'];
  if (oracleResult && typeof oracleResult === 'object') {
    return oracleResult as OracleResultV1;
  }
  return sf as OracleResultV1;
}

function pickString(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function payloadRecord(req: ApprovalRequest): Record<string, unknown> {
  const payload = req.payload;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return {};
  return payload as Record<string, unknown>;
}

const correctionLabels: Record<string, string> = {
  SUPPLIER_NOT_FOUND: 'Supplier not found',
  SUPPLIER_AMBIGUOUS: 'Supplier ambiguous',
  MULTIPLE_GRNS: 'Multiple GRNs',
  GRN_NOT_FOUND: 'GRN not found',
  AMOUNT_MISMATCH: 'Amount mismatch',
  SERVICE_PREP: 'Service prep',
  POST_FAILED: 'Post failed',
  EXP_REB_REVIEW: 'Exp/Reb review',
  MIXED_PI_BOOK_CONTEXT: 'goods posting context issue',
};

function correctionNote(req: ApprovalRequest): string | null {
  const payload = payloadRecord(req);
  const raw = payload['correction_bucket'];
  if (typeof raw !== 'string' || !raw || raw === 'POSTED') return null;
  const label = correctionLabels[raw] ?? humanizeBucket(raw).toLowerCase();
  if (req.bucket === 'POSTED_SERVICE') {
    return `Posted as service invoice after resolving ${label}.`;
  }
  return `Posted after resolving ${label}.`;
}

export function PostedView({ req, embedded }: PostedViewProps) {
  const result = readOracleResult(req);
  const summary = (req.summary ?? {}) as Record<string, unknown>;
  const refNo = pickString(result.ref_no ?? result.oracle_purch_invoice_ref_no);
  const documentNo = pickString(result.oracle_document_no ?? result.document_no);
  const piId = pickString(
    result.purchase_invoice_id ?? result.oracle_purchase_invoice_id ?? summary['oracle_pi_id'],
  );
  const glDate = formatDate(
    result.gl_date ?? result.oracle_gl_date ?? summary['oracle_gl_date'] ?? null,
  );
  const dueDate = formatDate(
    result.due_date ?? result.oracle_due_date ?? summary['oracle_due_date'] ?? null,
  );
  const verifiedAt = formatDate(
    result.post_verified_at ?? result.oracle_post_verified_at ?? null,
  );
  const postedAt = formatDate(readResolvedAt(req));
  const processedBy = readProcessedBy(req);
  const note = correctionNote(req);

  const content = (
    <>
      {note && <p className="text-sm text-neutral-700">{note}</p>}
      <section className="rounded-lg border border-success-500/30 bg-success-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-success-700">
          Posted to Oracle
        </p>
        <div className="mt-12 grid gap-12 tablet:grid-cols-2">
          <Readout label="PI Ref" value={refNo} />
          <Readout label="Document no" value={documentNo} />
          <Readout label="PI ID" value={piId} />
          <Readout label="GL date" value={glDate} />
          <Readout label="Due date" value={dueDate} />
          <Readout label="Verified at" value={verifiedAt} />
          <Readout label="Posted at" value={postedAt} />
          <Readout label="Processed by" value={processedBy} />
        </div>
      </section>
    </>
  );

  return embedded ? content : <BucketShell req={req}>{content}</BucketShell>;
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-success-500/20 bg-neutral-0 p-12">
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="mt-4 truncate text-sm font-medium text-neutral-900" title={value}>
        {value || '—'}
      </p>
    </div>
  );
}
